import React, { useState, useEffect } from 'react';
import { Radio, Input, Button } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';

const colorAttributesConfig = [
  { key: 'None', label: 'None', isDate: false },
  
  // General
  { key: 'milestoneNumber', label: '#', group: 'General', isDate: false },
  { key: 'name', label: 'Name', group: 'General', isDate: false },
  { key: 'milestoneNumber - name', label: '# - Name', group: 'General', isDate: false },
  { key: 'Owner', label: 'Milestone Owner', group: 'General', isDate: false },
  { key: 'status', label: 'Status', group: 'General', isDate: false },
  { key: 'type', label: 'Type', group: 'General', isDate: false },
  
  // Dates
  { key: 'Work starting date', label: 'Work starting date', group: 'Dates', isDate: true },
  { key: 'Milestone completion date', label: 'Milestone completion date', group: 'Dates', isDate: true },
  
  // Milestone Start Date Fiscal Periods
  { key: 'milestoneStartYear', label: '  └ Year', group: 'Dates', isDate: true, parentDate: 'Work starting date', isChild: true },
  { key: 'milestoneStartQuarter', label: '  └ Quarter', group: 'Dates', isDate: true, parentDate: 'Work starting date', isChild: true },
  { key: 'milestoneStartMonth', label: '  └ Month', group: 'Dates', isDate: true, parentDate: 'Work starting date', isChild: true },
  { key: 'milestoneStartWeek', label: '  └ Week', group: 'Dates', isDate: true, parentDate: 'Work starting date', isChild: true },
  
  // Milestone End Date Fiscal Periods
  { key: 'milestoneEndYear', label: '  └ Year', group: 'Dates', isDate: true, parentDate: 'Milestone completion date', isChild: true },
  { key: 'milestoneEndQuarter', label: '  └ Quarter', group: 'Dates', isDate: true, parentDate: 'Milestone completion date', isChild: true },
  { key: 'milestoneEndMonth', label: '  └ Month', group: 'Dates', isDate: true, parentDate: 'Milestone completion date', isChild: true },
  { key: 'milestoneEndWeek', label: '  └ Week', group: 'Dates', isDate: true, parentDate: 'Milestone completion date', isChild: true },
  
  // Milestone Created On Fiscal Periods
  { key: 'Created On', label: 'Created on', group: 'Audit', isDate: true },
  { key: 'milestoneCreatedOnYear', label: '  └ Year', group: 'Audit', isDate: true, parentDate: 'Created On', isChild: true },
  { key: 'milestoneCreatedOnQuarter', label: '  └ Quarter', group: 'Audit', isDate: true, parentDate: 'Created On', isChild: true },
  { key: 'milestoneCreatedOnMonth', label: '  └ Month', group: 'Audit', isDate: true, parentDate: 'Created On', isChild: true },
  { key: 'milestoneCreatedOnWeek', label: '  └ Week', group: 'Audit', isDate: true, parentDate: 'Created On', isChild: true },
  
  { key: 'Created by', label: 'Created by', group: 'Audit', isDate: false },
  
  // Milestone Updated On Fiscal Periods
  { key: 'Updated on', label: 'Updated on', group: 'Audit', isDate: true },
  { key: 'milestoneUpdatedOnYear', label: '  └ Year', group: 'Audit', isDate: true, parentDate: 'Updated on', isChild: true },
  { key: 'milestoneUpdatedOnQuarter', label: '  └ Quarter', group: 'Audit', isDate: true, parentDate: 'Updated on', isChild: true },
  { key: 'milestoneUpdatedOnMonth', label: '  └ Month', group: 'Audit', isDate: true, parentDate: 'Updated on', isChild: true },
  { key: 'milestoneUpdatedOnWeek', label: '  └ Week', group: 'Audit', isDate: true, parentDate: 'Updated on', isChild: true },
  
  { key: 'Updated by', label: 'Updated by', group: 'Audit', isDate: false },
  
  // Planning
  { key: 'Planning summary (simplified)', label: 'Planning summary (simplified)', group: 'Planning', isDate: false },
  { key: 'planningSummary', label: 'Planning summary', group: 'Planning', isDate: false },
  { key: '(LW) Planning summary', label: '(LW) Planning summary', group: 'Planning', isDate: false },
  { key: 'planningCheck', label: 'Planning check', group: 'Planning', isDate: false },
  
  // Change
  {
    key: 'Change in Milestone completion date (binned, current vs last week)',
    label: 'Change in Milestone completion date (binned, current vs last week)',
    group: 'Change',
    isDate: false,
  },
];

const MilestoneColorContent = ({ colorByAttribute, setColorByAttribute, selectedMenu }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isClearVisible, setIsClearVisible] = useState(false);
  const [expandedDates, setExpandedDates] = useState({});

  const handleClear = () => {
    setColorByAttribute('None');
    setExpandedDates({});
  };

  useEffect(() => {
    setIsClearVisible(colorByAttribute !== 'None' && colorByAttribute !== null);
  }, [colorByAttribute]);

  const filteredAttributes = colorAttributesConfig.filter((attribute) =>
    attribute.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedAttributes = {};
  filteredAttributes.forEach((attr) => {
    if (attr.group) {
      if (!groupedAttributes[attr.group]) {
        groupedAttributes[attr.group] = [];
      }
      groupedAttributes[attr.group].push(attr);
    }
  });

  // Check if we're in TableView mode
  const isTableView = selectedMenu?.key === 'Table';

  const toggleDateExpand = (attrKey) => {
    setExpandedDates(prev => ({
      ...prev,
      [attrKey]: !prev[attrKey]
    }));
  };

  // Get child attributes for a parent date
  const getChildAttributes = (parentKey) => {
    return colorAttributesConfig.filter(attr => attr.parentDate === parentKey && attr.isChild);
  };

  // Check if a parent has any child selected
  const hasChildSelected = (parentKey) => {
    const children = getChildAttributes(parentKey);
    return children.some(child => child.key === colorByAttribute);
  };

  // Sort groups for consistent display
  const sortedGroups = ['General', 'Dates', 'Audit', 'Planning', 'Change'];

  return (
    <div style={{ padding: '10px', maxHeight: '500px', overflowY: 'auto' }}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <Input
          placeholder="Search attributes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{ flex: 1 }}
        />
        {isClearVisible && (
          <Button
            type="text"
            onClick={handleClear}
            style={{ padding: '0 8px', color: '#ff4d4f' }}
            icon={<CloseCircleOutlined />}
          >
            Clear
          </Button>
        )}
      </div>

      <Radio.Group value={colorByAttribute} onChange={(e) => setColorByAttribute(e.target.value)} style={{ width: '100%' }}>
        {sortedGroups.map((groupName) => {
          const attributes = groupedAttributes[groupName];
          if (!attributes || attributes.length === 0) return null;
          
          return (
            <div key={groupName}>
              <div style={{ 
                fontWeight: 'bold', 
                marginTop: '12px', 
                marginBottom: '8px',
                fontSize: 13, 
                color: '#1890ff',
                borderBottom: '1px solid #f0f0f0',
                paddingBottom: '4px'
              }}>
                {groupName}
              </div>
              {attributes.map((attr) => {
                // Skip child attributes - they will be shown under their parent when expanded
                if (attr.isChild) return null;
                
                const childAttributes = getChildAttributes(attr.key);
                const isExpanded = expandedDates[attr.key];
                const isSelected = colorByAttribute === attr.key || hasChildSelected(attr.key);
                
                return (
                  <div key={attr.key} style={{ marginBottom: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '16px' }}>
                      <Radio 
                        value={attr.key} 
                        style={{ display: 'block' }}
                        disabled={attr.key === 'None'}
                        checked={isSelected}
                      >
                        <span style={{ fontSize: '13px' }}>{attr.label}</span>
                      </Radio>
                      {isTableView && attr.isDate && childAttributes.length > 0 && (
                        <Button
                          type="link"
                          size="small"
                          onClick={() => toggleDateExpand(attr.key)}
                          style={{ marginLeft: '8px', padding: '0 4px' }}
                        >
                          {isExpanded ? '−' : '+'}
                        </Button>
                      )}
                    </div>
                    
                    {/* Show child date attributes when expanded */}
                    {isTableView && attr.isDate && isExpanded && childAttributes.length > 0 && (
                      <div style={{ paddingLeft: '32px', marginTop: '5px', marginBottom: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                          Fiscal Period Groupings:
                        </div>
                        {childAttributes.map((child) => (
                          <div key={child.key} style={{ marginBottom: '4px', marginLeft: '8px' }}>
                            <Radio 
                              value={child.key}
                              style={{ display: 'block' }}
                              checked={colorByAttribute === child.key}
                            >
                              <span style={{ fontSize: '12px' }}>{child.label}</span>
                            </Radio>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </Radio.Group>
      
      {colorByAttribute && colorByAttribute !== 'None' && (
        <div style={{ 
          marginTop: '16px', 
          padding: '8px', 
          backgroundColor: '#f6f6f6', 
          borderRadius: '4px',
          fontSize: '12px',
          color: '#666'
        }}>
          <span>Color coding active for: <strong>{colorAttributesConfig.find(attr => attr.key === colorByAttribute)?.label || colorByAttribute}</strong></span>
        </div>
      )}
    </div>
  );
};

export default MilestoneColorContent;